import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';

const useBulkActions = ({ table, bulkActions, onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleBulkAction = useCallback(
    async (actionConfig, selectedRows) => {
      // action validation
      if (!actionConfig?.handler || selectedRows.length === 0) return;

      setIsLoading(true);

      try {
        const handlerResponse = await actionConfig.handler(selectedRows);
        const { result, shouldRefetch = true } =
          typeof handlerResponse === 'object' && handlerResponse !== null
            ? handlerResponse
            : { result: handlerResponse, shouldRefetch: true };

        // reset selected row
        table.toggleAllRowsSelected(false);

        if (!actionConfig?.skipSuccessToast) {
          toast.success(
            actionConfig.successMessage ||
              `Successfully processed ${selectedRows.length} items`,
          );
        }

        actionConfig.onSuccess?.(handlerResponse);

        if (onSuccess && shouldRefetch) {
          onSuccess({
            action: actionConfig.value,
            successCount: selectedRows.length,
            rows: selectedRows,
            result,
          });
        }

        return result;
      } catch (error) {
        toast.error(
          actionConfig.errorMessage ||
            error.message ||
            'Failed to process bulk action',
        );
      } finally {
        setIsLoading(false);
      }
    },
    [table, onSuccess],
  );

  const executeBulkAction = useCallback(
    async actionValue => {
      if (!bulkActions) return;

      const selectedRows = table
        .getSelectedRowModel()
        .rows.map(row => row.original);

      // 실행시킬 action
      const actionConfig = bulkActions.find(
        action => action.value === actionValue,
      );

      if (!actionConfig) {
        return;
      }

      try {
        await handleBulkAction(actionConfig, selectedRows);
      } catch (error) {
        toast.error('Bulk action execution failed');
      }
    },
    [table, bulkActions, handleBulkAction],
  );

  return {
    isLoading,
    executeBulkAction,
  };
};

export default useBulkActions;
